import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { 
  Terminal,
  Database,
  Cloud,
  GitBranch,
  Shield,
  Cpu,
  Package,
  Activity,
  FileJson,
  Server,
  Lock,
  Zap
} from 'lucide-react'
import { CodeDemo } from '../ui/CodeDemo'

interface TechFeature {
  id: string
  title: string
  description: string
  icon: React.ElementType
  code?: string
  language?: string
}

export const TechShowcaseSection: React.FC = () => {
  const { t } = useTranslation('techShowcase')
  
  // Função para mapear IDs para ícones
  function getIconForFeature(id: string): React.ElementType {
    const iconMap: Record<string, React.ElementType> = {
      'form-builder': FileJson,
      'dynamic-registry': GitBranch,
      'field-mapper': Package,
      'ai-integration': Cpu
    }
    return iconMap[id] || FileJson
  }

  // Códigos de exemplo para cada feature
  const codeExamples: Record<string, { code: string; language: string }> = {
    'form-builder': {
      language: 'typescript',
      code: `// GET /api/form-builder/definitions
const formDefinition = await api.post('/form-builder/definitions', {
  entityType: 'produto',
  complexity: 'MEDIUM',
  form_structure: {
    tabs: [
      {
        id: 'dados-basicos',
        label: 'Dados Básicos',
        fields: [
          { name: 'nome', type: 'TEXT', required: true },
          { name: 'preco', type: 'CURRENCY', validation: { min: 0.01 } },
          { name: 'categoria', type: 'LOOKUP', endpoint: '/api/categorias' }
        ]
      }
    ]
  }
});

// Formulário criado instantaneamente no frontend
console.log('Form ID:', formDefinition.id);`
    },
    'dynamic-registry': {
      language: 'java',
      code: `@Component
public class DynamicFormRegistry {
    @Autowired 
    private DynamicFormService dynamicFormService;
    
    /**
     * 100% Dynamic Form Configuration
     * Sem fallbacks estáticos - Pure Innovation
     */
    public FormConfig getConfig(String entityType) {
        log.debug("Loading 100% dynamic config for: {}", entityType);
        
        // Busca configuração dinâmica diretamente do banco
        return dynamicFormService.getFormConfig(entityType);
    }
    
    @Cacheable("dynamic-forms")
    public FormConfig getCachedConfig(String entityType) {
        return getConfig(entityType);
    }
    
    // Criação de formulários em tempo real
    public FormConfig createInstantForm(FormDefinitionDTO dto) {
        return dynamicFormService.createAndActivate(dto);
    }
}`
    },
    'field-mapper': {
      language: 'typescript',
      code: `const FieldTypeMapper = {
  mapFieldToComponent(field: FieldDefinition): ComponentConfig {
    const mapping = {
      TEXT: { component: 'q-input', props: { type: 'text' } },
      NUMBER: { component: 'q-input', props: { type: 'number' } },
      CURRENCY: { component: 'q-currency', props: { prefix: 'R$' } },
      DATE: { component: 'q-date', props: { mask: 'DD/MM/YYYY' } },
      LOOKUP: { 
        component: 'lookup-select',
        props: { 
          endpoint: field.endpoint,
          displayField: field.displayField,
          lazy: true 
        }
      },
      TABLE: {
        component: 'q-table',
        props: { 
          columns: field.columns,
          editable: true 
        }
      }
    };
    
    return mapping[field.type] || mapping.TEXT;
  }
};`
    },
    'ai-integration': {
      language: 'java',
      code: `@Service
public class AIAssistantService {
    @Value("\${openai.api.key}")
    private String apiKey;
    
    public String processQuery(String userQuery, Context context) {
        ChatCompletionRequest request = ChatCompletionRequest.builder()
            .model("gpt-4")
            .messages(Arrays.asList(
                new ChatMessage("system", getSystemPrompt(context)),
                new ChatMessage("user", userQuery)
            ))
            .temperature(0.7)
            .build();
            
        ChatCompletionResult response = openAiService
            .createChatCompletion(request);
            
        return response.getChoices().get(0)
            .getMessage().getContent();
    }
    
    @Async
    public CompletableFuture<String> generateDescription(Product product) {
        return CompletableFuture.completedFuture(
            processQuery("Gere uma descrição para: " + product.getName())
        );
    }
}`
    }
  }

  // Buscar features traduzidas do i18n
  const techFeatures: TechFeature[] = (t('features', { returnObjects: true }) as any[]).map((feature: any) => ({
    ...feature,
    icon: getIconForFeature(feature.id),
    code: codeExamples[feature.id]?.code,
    language: codeExamples[feature.id]?.language
  }))

  // Architecture stack com ícones
  const architectureStackIcons: Record<string, React.ElementType> = {
    'Backend': Server,
    'Database': Database,
    'Frontend': Package,
    'DevOps': Cloud,
    'Security': Lock,
    'CI/CD': GitBranch
  }

  const architectureStack = (t('architectureStack.technologies', { returnObjects: true }) as any[]).map((tech: any) => ({
    ...tech,
    icon: architectureStackIcons[tech.category] || Terminal
  }))

  const [selectedFeature, setSelectedFeature] = useState<TechFeature>(techFeatures[0])

  return (
    <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-kylix-primary text-white">
      <div className="container">
        {/* Header */}
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold mb-4"
          >
            {t('header.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            {t('header.description')}
          </motion.p>
        </div>

        {/* Feature Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {techFeatures.map((feature) => (
            <motion.button
              key={feature.id}
              onClick={() => setSelectedFeature(feature)}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                selectedFeature.id === feature.id
                  ? 'bg-kylix-secondary text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <feature.icon className="w-4 h-4" />
              {feature.title}
            </motion.button>
          ))}
        </div>

        {/* Code Display */}
        <motion.div
          key={selectedFeature.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Description */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-3 flex items-center gap-3">
                  <selectedFeature.icon className="w-8 h-8 text-kylix-secondary" />
                  {selectedFeature.title}
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  {selectedFeature.description}
                </p>
              </div>

              {/* Key Benefits */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-yellow-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{t('benefits.performance.title')}</p>
                    <p className="text-sm text-gray-400">{t('benefits.performance.description')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-green-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{t('benefits.security.title')}</p>
                    <p className="text-sm text-gray-400">{t('benefits.security.description')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Activity className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{t('benefits.monitoring.title')}</p>
                    <p className="text-sm text-gray-400">{t('benefits.monitoring.description')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Code Block */}
            <div className="lg:col-span-3">
              <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-700">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="text-sm text-gray-400 font-mono">
                    {selectedFeature.language || 'typescript'}
                  </span>
                </div>
                <div className="max-h-96 overflow-auto">
                  <CodeDemo 
                    code={selectedFeature.code || '// Code example'} 
                    language={selectedFeature.language || 'typescript'}
                  />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Architecture Stack */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl font-bold text-center mb-12">
            {t('architectureStack.title')}
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {architectureStack.map((tech, index) => (
              <motion.div
                key={tech.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center hover:bg-white/20 transition-colors"
              >
                <tech.icon className="w-8 h-8 mx-auto mb-3 text-kylix-secondary" />
                <p className="font-semibold text-sm">{tech.name}</p>
                <p className="text-xs text-gray-400 mt-1">{tech.category}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  )
}